import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import { usePageTitle } from '../../components/shell/AppShell';
import {
  useClearAcesSetting,
  useClearOperatorSetting,
  useEffectiveSettings,
  useMe,
  useMyPermissions,
  useSetAcesSetting,
  useSetOperatorSetting,
} from '../../api/queries';
import type { EffectiveSetting } from '../../api/types';

/**
 * /settings/system — flat list of every system setting, grouped by
 * category. Each row shows definition + effective value + a source pill
 * (default / aces / operator). Edit gated by:
 *   - settings.operator.edit  → write the operator-scope override
 *   - settings.aces.edit      → write the aces-scope override
 *
 * Definitions themselves are read-only (seeded by release migrations).
 */
export default function SystemSettingsPage() {
  usePageTitle('System settings');
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const { data, isLoading, error } = useEffectiveSettings();
  const [editTarget, setEditTarget] = useState<EffectiveSetting | null>(null);

  // Group by category, preserve display order within category.
  const grouped = useMemo(() => {
    const map = new Map<string, EffectiveSetting[]>();
    for (const s of data ?? []) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.displayOrder - b.displayOrder);
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4">System settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Effective values resolve <code>operator</code> override → <code>aces</code> override → seeded default.
            Definitions themselves are immutable from the portal — they're managed via release migrations.
          </Typography>
        </Box>

        {isLoading ? (
          <Stack direction="row" spacing={2}><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
        ) : error ? (
          <ErrorAlert>{(error as { message?: string }).message}</ErrorAlert>
        ) : grouped.length === 0 ? (
          <Alert severity="info">No system settings defined.</Alert>
        ) : (
          grouped.map(([category, settings]) => (
            <Box key={category}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {category}
              </Typography>
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {settings.map((s) => (
                  <SettingRow
                    key={s.key}
                    setting={s}
                    canEdit={s.source !== 'aces' || isArms ? (
                      // operator override: any user with operator-edit can change unless the
                      // definition says operator-can-override = false
                      (has('settings.operator.edit') && s.operatorCanOverride && !isArms) ||
                      has('settings.aces.edit')
                    ) : has('settings.aces.edit')}
                    onEdit={() => setEditTarget(s)}
                  />
                ))}
              </Stack>
            </Box>
          ))
        )}

        <EditSettingDialog
          setting={editTarget}
          onClose={() => setEditTarget(null)}
          isArms={isArms}
        />
      </Stack>
    </>
  );
}

function SettingRow({ setting, canEdit, onEdit }: {
  setting: EffectiveSetting;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 120px 100px' }, gap: 2, alignItems: 'center' }}>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {setting.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
          {setting.key}
        </Typography>
        {setting.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {setting.description}
          </Typography>
        )}
      </Box>
      <Box>
        <Typography variant="body2" component="code" sx={{
          fontFamily: 'monospace',
          bgcolor: 'action.hover',
          px: 0.75, py: 0.25, borderRadius: 0.5,
          display: 'inline-block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {setting.isSecret ? '***' : setting.value}
        </Typography>
        {setting.dataType && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            type: {setting.dataType}
          </Typography>
        )}
      </Box>
      <SourceChip source={setting.source} />
      <Box sx={{ textAlign: 'right' }}>
        {canEdit && (
          <Button size="small" onClick={onEdit}>Edit</Button>
        )}
      </Box>
    </Box>
  );
}

function SourceChip({ source }: { source: string }) {
  const color =
    source === 'operator' ? 'primary' :
    source === 'aces'     ? 'secondary' :
    'default';
  return <Chip size="small" variant="outlined" label={source}
               color={color as 'primary' | 'secondary' | 'default'} />;
}

function EditSettingDialog({ setting, onClose, isArms }: {
  setting: EffectiveSetting | null;
  onClose: () => void;
  isArms: boolean;
}) {
  const open = setting !== null;
  const [scope, setScope] = useState<'operator' | 'aces'>('operator');
  const [value, setValue] = useState('');
  const setOperator = useSetOperatorSetting();
  const clearOperator = useClearOperatorSetting();
  const setAces = useSetAcesSetting();
  const clearAces = useClearAcesSetting();

  // Re-initialise local edit state whenever the dialog opens for a different
  // setting. (Initial render sees a null setting; the effect fires once the
  // parent passes one in.)
  useEffect(() => {
    if (setting) {
      setValue(setting.value);
      setScope(setting.source === 'aces' ? 'aces' : 'operator');
    }
  }, [setting]);

  if (!setting) return <Dialog open={false} onClose={onClose}><span/></Dialog>;

  const busy = setOperator.isPending || setAces.isPending || clearOperator.isPending || clearAces.isPending;
  const err = (setOperator.error || setAces.error || clearOperator.error || clearAces.error) as { message?: string } | null;

  const onSave = () => {
    const args = { key: setting.key, value };
    if (scope === 'aces') setAces.mutate(args, { onSuccess: onClose });
    else setOperator.mutate(args, { onSuccess: onClose });
  };

  const onReset = () => {
    if (scope === 'aces') clearAces.mutate({ key: setting.key }, { onSuccess: onClose });
    else clearOperator.mutate({ key: setting.key }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack>
          <Typography variant="h6">{setting.displayName}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {setting.key}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} disabled={busy}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {setting.description && (
            <Typography variant="body2" color="text.secondary">{setting.description}</Typography>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Default</Typography>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
              {setting.defaultValue ?? '—'}
            </Typography>
            {setting.floor !== null && (<>
              <Typography variant="body2" color="text.secondary">Floor</Typography>
              <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>{setting.floor}</Typography>
            </>)}
            {setting.ceiling !== null && (<>
              <Typography variant="body2" color="text.secondary">Ceiling</Typography>
              <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>{setting.ceiling}</Typography>
            </>)}
            <Typography variant="body2" color="text.secondary">Type</Typography>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>{setting.dataType}</Typography>
          </Box>

          {isArms && (
            <TextField
              select
              label="Override scope"
              size="small"
              value={scope}
              onChange={(e) => setScope(e.target.value as 'operator' | 'aces')}
              helperText={scope === 'aces'
                ? 'aces — applies platform-wide unless an operator overrides it.'
                : 'operator — applies to a single operator only.'}
            >
              <MenuItem value="operator">Operator scope</MenuItem>
              <MenuItem value="aces">ARMS scope (platform-wide)</MenuItem>
            </TextField>
          )}

          {setting.enumValues && setting.enumValues.length > 0 ? (
            <TextField
              select label="Value" size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              {setting.enumValues.map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </TextField>
          ) : setting.dataType === 'bool' ? (
            <TextField
              select label="Value" size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </TextField>
          ) : (
            <TextField
              label="Value" size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              type={setting.dataType === 'int' ? 'number' : 'text'}
              multiline={setting.dataType === 'json'}
              minRows={setting.dataType === 'json' ? 4 : undefined}
            />
          )}

          {err?.message && <ErrorAlert>{err.message}</ErrorAlert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button startIcon={<RestoreIcon />} color="warning" onClick={onReset} disabled={busy}>
          Reset to default
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

